import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/campaigns", label: t("nav.campaigns") },
    { to: "/suggest-campaign", label: t("nav.suggest") },
    { to: "/membership", label: t("nav.membership") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-cream/70 border-b border-forest/10">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-3xl font-semibold tracking-tight text-forest group-hover:text-clay transition-colors duration-300">Future Gen</span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-sage font-medium">ASBL</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-clay ${location.pathname === l.to ? "text-clay" : "text-forest"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div data-testid="language-switcher" className="flex items-center border border-forest/20 rounded-full overflow-hidden">
            {["en", "fr", "de", "tr"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1.5 text-xs font-semibold tracking-widest transition-colors duration-300 ${lang === l ? "bg-forest text-cream" : "text-forest hover:bg-forest/5"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <Link
            to="/campaigns"
            data-testid="nav-donate-btn"
            className="hidden md:inline-flex items-center gap-2 bg-clay text-cream text-sm font-medium px-6 py-2.5 rounded-full hover:bg-clay-hover hover:-translate-y-0.5 transition-transform duration-300"
          >
            {t("nav.donate")}
            <ArrowUpRight size={16} />
          </Link>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden text-forest p-2">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-forest/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-serif text-2xl text-forest hover:text-clay transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/campaigns" onClick={() => setOpen(false)} data-testid="mobile-nav-donate-btn" className="inline-flex w-fit items-center gap-2 bg-clay text-cream px-6 py-3 rounded-full font-medium">
                {t("nav.donate")}
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
